import type { FunctionComponent } from 'react';
import { Suspense, useMemo } from 'react';

import { dontUpsell } from '@trello/browser';
import { PremiumFeatures } from '@trello/entitlements';
import {
  ChunkLoadErrorBoundary,
  ErrorBoundary,
} from '@trello/error-boundaries';
import { useFeatureFlag } from '@trello/feature-flag-client';
import { intl } from '@trello/i18n/provider';
import { useBoardId, useWorkspaceId } from '@trello/id-context';
import {
  PopoverSection,
  usePersistentCollapsiblePopoverSection,
} from '@trello/nachos/experimental-popover-section';
import { PopoverMenuItemSeparator } from '@trello/nachos/popover-menu';
import { useLazyComponent } from '@trello/use-lazy-component';

import { useBoardPremiumFeaturesFragment } from 'app/src/components/FileSizeAlert/BoardPremiumFeaturesFragment.generated';
import { ListColorPickerSkeleton } from './ListColorPickerSkeleton';
import { ListColorPickerUpgradePrompt } from './ListColorPickerUpgradePrompt';

interface LazyListColorPickerProps {
  onHide?: () => void;
  /**
   * TEMPORARY: Gates some padding changes that should be made default once
   * xf.list-actions-popover-modernization ships.
   */
  isListActionsPopoverModernizationEnabled?: boolean;
}

export const LazyListColorPicker: FunctionComponent<
  LazyListColorPickerProps
> = ({ onHide, isListActionsPopoverModernizationEnabled }) => {
  const boardId = useBoardId();
  const workspaceId = useWorkspaceId();

  const { data } = useBoardPremiumFeaturesFragment({ from: { id: boardId } });
  const hasPremiumFeature = useMemo(
    () => data?.premiumFeatures.includes(PremiumFeatures.listColors),
    [data?.premiumFeatures],
  );

  const ListColorPicker = useLazyComponent(
    () =>
      import(/* webpackChunkName: "list-color-picker" */ './ListColorPicker'),
    { namedImport: 'ListColorPicker' },
  );

  const canShowUpsell = useFeatureFlag('workflowers.list-colors.upsell', false);

  const collapsiblePopoverSectionProps = usePersistentCollapsiblePopoverSection(
    'listActionsPopoverColorPicker',
  );

  // The upsell flow requires a workspace ID, so if the user doesn't have access
  // to the feature or a workspace ID, bail out. This is an edge case.
  if (!hasPremiumFeature && (dontUpsell() || !canShowUpsell || !workspaceId)) {
    return null;
  }

  return (
    <ErrorBoundary
      tags={{
        ownershipArea: 'trello-web-eng',
        feature: 'List Color Picker',
      }}
    >
      <PopoverMenuItemSeparator fullWidth={true} />

      <PopoverSection
        {...collapsiblePopoverSectionProps}
        title={intl.formatMessage({
          id: 'templates.list_menu.change-list-color',
          defaultMessage: 'Change list color',
          description: 'Header for the list color picker section',
        })}
        isCollapsible={isListActionsPopoverModernizationEnabled}
      >
        {hasPremiumFeature ? (
          <ChunkLoadErrorBoundary fallback={null}>
            <Suspense fallback={<ListColorPickerSkeleton />}>
              <ListColorPicker />
            </Suspense>
          </ChunkLoadErrorBoundary>
        ) : (
          <ListColorPickerUpgradePrompt onClickCta={onHide} />
        )}
      </PopoverSection>
    </ErrorBoundary>
  );
};
